<template>
  <div class="rich-text-field">
    <label v-if="label" class="rich-text-label">{{ label }}</label>
    <div class="rich-text-toolbar">
      <button
        type="button"
        class="editor-tool"
        title="Bold"
        @mousedown.prevent
        @click="runCommand('bold')"
      >
        <i class="fa fa-bold"></i>
      </button>
      <button
        type="button"
        class="editor-tool"
        title="Italic"
        @mousedown.prevent
        @click="runCommand('italic')"
      >
        <i class="fa fa-italic"></i>
      </button>
      <button
        type="button"
        class="editor-tool"
        title="Underline"
        @mousedown.prevent
        @click="runCommand('underline')"
      >
        <i class="fa fa-underline"></i>
      </button>
      <span class="editor-divider"></span>
      <button
        type="button"
        class="editor-tool"
        title="Heading"
        @mousedown.prevent
        @click="runCommand('formatBlock', 'H2')"
      >
        H2
      </button>
      <button
        type="button"
        class="editor-tool"
        title="Paragraph"
        @mousedown.prevent
        @click="runCommand('formatBlock', 'P')"
      >
        P
      </button>
      <select
        class="editor-font-size"
        title="Font size"
        aria-label="Font size"
        @mousedown="saveSelection"
        @change="applyFontSize"
      >
        <option value="">Font size</option>
        <option
          v-for="fontSize in fontSizes"
          :key="fontSize.value"
          :value="fontSize.value"
        >
          {{ fontSize.label }}
        </option>
      </select>
      <span class="editor-divider"></span>
      <button
        type="button"
        class="editor-tool"
        title="Bulleted list"
        @mousedown.prevent
        @click="runCommand('insertUnorderedList')"
      >
        <i class="fa fa-list-ul"></i>
      </button>
      <button
        type="button"
        class="editor-tool"
        title="Numbered list"
        @mousedown.prevent
        @click="runCommand('insertOrderedList')"
      >
        <i class="fa fa-list-ol"></i>
      </button>
      <button
        type="button"
        class="editor-tool"
        title="Add link"
        @mousedown.prevent
        @click="createLink"
      >
        <i class="fa fa-link"></i>
      </button>
      <button
        type="button"
        class="editor-tool"
        title="Clear formatting"
        @mousedown.prevent
        @click="runCommand('removeFormat')"
      >
        <i class="fa fa-eraser"></i>
      </button>
    </div>
    <div
      ref="editor"
      class="rich-text-editor"
      contenteditable="true"
      :data-placeholder="placeholder"
      @blur="onBlur"
      @input="onInput"
      @keyup="saveSelection"
      @mouseup="saveSelection"
      @paste="onPaste"
    ></div>
  </div>
</template>

<script>
export default {
  name: "RichTextEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Start writing...",
    },
  },
  data() {
    return {
      fontSizes: [
        { label: "12 px", value: "12px" },
        { label: "14 px", value: "14px" },
        { label: "16 px", value: "16px" },
        { label: "18 px", value: "18px" },
        { label: "20 px", value: "20px" },
        { label: "24 px", value: "24px" },
        { label: "28 px", value: "28px" },
        { label: "32 px", value: "32px" },
        { label: "36 px", value: "36px" },
        { label: "48 px", value: "48px" },
      ],
      pendingFontSize: "",
      savedRange: null,
    };
  },
  watch: {
    value() {
      this.syncFromValue();
    },
  },
  mounted() {
    this.syncFromValue();
  },
  methods: {
    syncFromValue() {
      if (!this.$refs.editor) return;
      if (this.$refs.editor.innerHTML !== this.value) {
        this.$refs.editor.innerHTML = this.value || "";
      }
    },
    emitContent() {
      if (!this.$refs.editor) return;
      this.$emit("input", this.$refs.editor.innerHTML);
    },
    onBlur() {
      this.saveSelection();
      this.emitContent();
    },
    onInput() {
      if (this.pendingFontSize) {
        const convertedElements = this.convertFontSizeMarkers(
          this.pendingFontSize
        );
        if (convertedElements) {
          this.pendingFontSize = "";
        }
      }

      this.saveSelection();
      this.emitContent();
    },
    runCommand(command, value) {
      this.$refs.editor.focus();
      document.execCommand(command, false, value || null);
      this.saveSelection();
      this.emitContent();
    },
    applyFontSize(event) {
      const fontSize = event.target.value;
      event.target.value = "";

      if (!fontSize || !this.$refs.editor) return;

      this.restoreSelection();
      this.$refs.editor.focus();
      this.pendingFontSize = fontSize;
      document.execCommand("fontSize", false, "7");

      if (this.convertFontSizeMarkers(fontSize)) {
        this.pendingFontSize = "";
      }

      this.saveSelection();
      this.emitContent();
    },
    convertFontSizeMarkers(fontSize) {
      if (!this.$refs.editor) return 0;

      const markers = Array.from(
        this.$refs.editor.querySelectorAll('font[size="7"]')
      );

      markers.forEach((fontElement) => {
        const span = document.createElement("span");

        Array.from(fontElement.attributes).forEach((attribute) => {
          if (attribute.name !== "size") {
            span.setAttribute(attribute.name, attribute.value);
          }
        });

        span.style.fontSize = fontSize;

        while (fontElement.firstChild) {
          span.appendChild(fontElement.firstChild);
        }

        fontElement.parentNode.replaceChild(span, fontElement);
      });

      return markers.length;
    },
    saveSelection() {
      const editor = this.$refs.editor;
      const selection = window.getSelection();

      if (!editor || !selection || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const selectedNode = range.commonAncestorContainer;
      if (selectedNode !== editor && !editor.contains(selectedNode)) return;

      this.savedRange = range.cloneRange();
    },
    restoreSelection() {
      if (!this.savedRange) return;

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    },
    createLink() {
      const url = window.prompt("Enter link URL");
      if (!url) return;

      this.runCommand("createLink", url);
    },
    onPaste(event) {
      event.preventDefault();
      const text =
        (event.clipboardData || window.clipboardData).getData("text/plain") ||
        "";
      document.execCommand("insertText", false, text);
      this.emitContent();
    },
  },
};
</script>

<style scoped>
.rich-text-field {
  margin-bottom: 24px;
}

.rich-text-label {
  color: #fff;
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}

.rich-text-toolbar {
  align-items: center;
  background: #27293d;
  border: 1px solid #2b3553;
  border-bottom: 0;
  border-radius: 0.4285rem 0.4285rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
}

.editor-tool {
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  height: 32px;
  justify-content: center;
  min-width: 32px;
  padding: 0 9px;
}

.editor-tool:hover {
  background: #344675;
  border-color: #e14eca;
}

.editor-font-size {
  background: #27293d;
  border: 1px solid #2b3553;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  height: 32px;
  padding: 0 8px;
}

.editor-font-size:focus,
.editor-font-size:hover {
  border-color: #e14eca;
  outline: none;
}

.editor-font-size option {
  background: #27293d;
  color: #fff;
}

.editor-divider {
  background: #2b3553;
  display: inline-block;
  height: 24px;
  margin: 0 4px;
  width: 1px;
}

.rich-text-editor {
  background: #000;
  border: 1px solid #2b3553;
  border-radius: 0 0 0.4285rem 0.4285rem;
  color: #fff;
  min-height: 280px;
  outline: none;
  padding: 14px 16px;
}

.rich-text-editor:empty:before {
  color: #6c757d;
  content: attr(data-placeholder);
}
</style>
